import type { TFunction } from 'i18next';
import { PerformanceReviewRound } from '../../../api/generated/model/performanceReviewRound';
import { PerformanceReviewRoundStage } from '../../../api/generated/model/performanceReviewRoundStage';
import { PerformanceReviewQuestionnaire } from '../../../api/generated/model/performanceReviewQuestionnaire';
import { PerformanceReviewQuestion } from '../../../api/generated/model/performanceReviewQuestion';
import { PerformanceReviewForm } from '../../../api/generated/model/performanceReviewForm';
import { Grade } from '../../../types';
import {
  DeadlineInfo,
  EssayBlock,
  ParticipantMe,
  PeerRelation,
  SummaryStageBlock,
  ViewStage,
} from './types';

export const stageOrder: PerformanceReviewRoundStage[] = [
  'collect-essay',
  'collect-feedback',
  'summary',
  'finished',
];

export const relationMeta: Record<PeerRelation, { i18nKey: string; color: string }> = {
  manager: { i18nKey: 'perfReview.home.feedback.relationManager', color: 'teal' },
  peer: { i18nKey: 'perfReview.home.feedback.relationPeer', color: 'indigo' },
  cross: { i18nKey: 'perfReview.home.feedback.relationCross', color: 'orange' },
};

export const formatPeriod = (start?: string, end?: string, locale = 'ru-RU') => {
  const fmt = (d?: string) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };
  const yr = end
    ? new Date(end).getFullYear()
    : start
      ? new Date(start).getFullYear()
      : new Date().getFullYear();
  return { start: fmt(start), end: fmt(end), year: String(yr) };
};

export const initials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();

export const relationFromFormType = (formType?: string): PeerRelation =>
  formType === 'manager' ? 'manager' : 'peer';

// Compute formatted "start — end" ranges for each stage from round.stageHistory.
// Past stages get a real start→end window (start of stage → start of next).
// The current stage gets start→roundEnd (best-known approximation). Future
// stages stay unscheduled.
type StageHistoryRecord = {
  stage?: PerformanceReviewRoundStage;
  transitionedAt?: string | Date;
};

// The configured per-stage deadline for a given stage (falls back to the
// round end for the terminal "finished" stage). Used so the UI shows the
// stage's own deadline instead of the round end.
const stageDeadlineIso = (
  round: PerformanceReviewRound,
  stage: PerformanceReviewRoundStage,
): string | undefined => {
  const sd = round.stageDeadlines;
  switch (stage) {
    case 'collect-essay':
      return sd?.collectEssay;
    case 'collect-feedback':
      return sd?.collectFeedback;
    case 'summary':
      return sd?.summary;
    case 'finished':
      return round.endDate;
    default:
      return undefined;
  }
};

export const computeStageWindows = (
  round: PerformanceReviewRound | undefined,
  t: TFunction,
  locale: string,
): Partial<Record<PerformanceReviewRoundStage, string>> => {
  const result: Partial<Record<PerformanceReviewRoundStage, string>> = {};
  if (!round) return result;
  const fmt = (d: Date) => d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const history = ((round.stageHistory as StageHistoryRecord[] | undefined) ?? [])
    .filter(
      (h): h is { stage: PerformanceReviewRoundStage; transitionedAt: string | Date } =>
        Boolean(h.stage) && Boolean(h.transitionedAt),
    )
    .sort((a, b) => +new Date(a.transitionedAt) - +new Date(b.transitionedAt));
  const roundEnd = round.endDate ? new Date(round.endDate) : null;
  for (let i = 0; i < history.length; i++) {
    const entry = history[i];
    if (entry.stage === 'draft') continue;
    const start = new Date(entry.transitionedAt);
    const next = history[i + 1];
    // Past stages: real start → next-transition window. Current (last) stage:
    // prefer its configured per-stage deadline, fall back to the round end.
    const stageDeadline = stageDeadlineIso(round, entry.stage);
    const end = next
      ? new Date(next.transitionedAt)
      : stageDeadline
        ? new Date(stageDeadline)
        : roundEnd;
    if (end) {
      result[entry.stage] = `${fmt(start)} — ${fmt(end)}`;
    } else {
      result[entry.stage] = t('perfReview.home.stageWindow.from', { date: fmt(start) });
    }
  }
  return result;
};

export const computeDeadline = (
  round: PerformanceReviewRound | undefined,
  stage: ViewStage,
  t: TFunction,
  locale: string,
): DeadlineInfo | null => {
  // У завершённого раунда считать уже нечего — карточки срока нет.
  if (!round || !round.endDate || stage === 'no-round' || stage === 'draft' || stage === 'finished')
    return null;
  const fmt = (d: Date) => d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const roundEnd = new Date(round.endDate);
  // Count down to the current stage's own deadline when it's configured and
  // earlier than the round end; otherwise fall back to the round end.
  const stageIso = stageDeadlineIso(round, stage as PerformanceReviewRoundStage);
  const end = stageIso ? new Date(stageIso) : roundEnd;
  const usesStageDeadline = !!stageIso && end.getTime() !== roundEnd.getTime();
  const msInDay = 1000 * 60 * 60 * 24;
  // Negative days mean the deadline has already passed — surface the overdue
  // state instead of clamping the countdown to zero.
  const days = Math.ceil((end.getTime() - Date.now()) / msInDay);
  const label =
    days < 0
      ? t('perfReview.home.deadline.deadlineWas')
      : usesStageDeadline
        ? t('perfReview.home.deadline.stageEnds')
        : t('perfReview.home.deadline.roundEnds');
  return {
    days,
    label,
    date: fmt(end),
  };
};

const isAnswered = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const computeEssayBlocks = (
  questionnaire: PerformanceReviewQuestionnaire | undefined,
  selfForm: PerformanceReviewForm | undefined,
  t: TFunction,
): EssayBlock[] => {
  const questions =
    (questionnaire?.selfForm?.questions as PerformanceReviewQuestion[] | undefined) ?? [];
  if (questions.length === 0) return [];
  const answeredIds = new Set(
    (selfForm?.answers ?? []).filter((a) => isAnswered(a.value)).map((a) => a.questionId),
  );
  const order: string[] = [];
  const acc = new Map<string, { filled: number; total: number }>();
  const fallbackTitle = t('perfReview.home.essay.blockGeneral');
  for (const q of questions) {
    const block = q.block?.trim() || fallbackTitle;
    if (!acc.has(block)) {
      acc.set(block, { filled: 0, total: 0 });
      order.push(block);
    }
    const g = acc.get(block)!;
    g.total++;
    if (q.id && answeredIds.has(q.id)) g.filled++;
  }
  return order.map((title, i) => {
    const g = acc.get(title)!;
    return { id: `b-${i}`, title, filled: g.filled, total: g.total };
  });
};

export const computeSummaryBlocks = (
  questionnaire: PerformanceReviewQuestionnaire | undefined,
  participant: ParticipantMe | undefined,
): SummaryStageBlock[] => {
  const questions =
    (questionnaire?.selfForm?.questions as PerformanceReviewQuestion[] | undefined) ?? [];
  const blocks = Array.from(
    new Set(questions.map((q) => q.block?.trim()).filter((b): b is string => !!b)),
  );
  if (blocks.length === 0) return [];
  const readyMap = new Map<string, boolean>();
  for (const sb of participant?.summaryBlocks ?? []) {
    // `ready` is authoritative — the backend withholds `comment` until the summary
    // is published, so inferring readiness from the text alone would report every
    // block as empty right up until publication.
    if (sb.block) readyMap.set(sb.block, sb.ready ?? !!sb.comment?.trim());
  }
  return blocks.map((title, i) => ({
    id: `sb-${i}`,
    title,
    ready: readyMap.get(title) ?? false,
  }));
};

export const decisionI18nKey = (d: ParticipantMe['decision']): string => {
  switch (d) {
    case 'promote':
      return 'perfReview.home.finished.decisionPromote';
    case 'confirm':
      return 'perfReview.home.finished.decisionConfirm';
    case 'demote':
      return 'perfReview.home.finished.decisionDemote';
    default:
      return 'perfReview.home.finished.decisionNoChange';
  }
};

export const computeDelta = (participant: ParticipantMe | null, t: TFunction): string => {
  const before = typeof participant?.gradeBefore === 'object' ? participant.gradeBefore : null;
  const after = typeof participant?.gradeAfter === 'object' ? participant.gradeAfter : null;
  const beforeLevel = (before as Grade | null)?.level;
  const afterLevel = (after as Grade | null)?.level;
  if (beforeLevel == null || afterLevel == null) {
    return participant?.decision ? t(decisionI18nKey(participant.decision)) : '';
  }
  const diff = afterLevel - beforeLevel;
  if (diff > 0) return t('perfReview.home.finished.deltaPlus', { n: diff });
  if (diff < 0) return t('perfReview.home.finished.deltaMinus', { n: Math.abs(diff) });
  return t('perfReview.home.finished.deltaSame');
};

// «Оценка за период» — вывод по всему ревью, а не ещё одна тема наравне с
// прочими. В анкете она стоит последней, и в результатах читатель добирался до
// неё, пролистав шесть частных блоков. Поднимаем наверх; порядок остальных не
// трогаем.
// Сверяем с полным именем, а не с префиксом: блок общей оценки всегда приходит
// из константы GENERAL_SCORE_BLOCK на бэке, а вот swimlane карты компетенций
// вполне может называться «Общие компетенции» или «Общение с заказчиком» — по
// прежнему префиксу `^общ` такой блок уезжал наверх и читался как вывод по
// всему ревью.
// Имён два, потому что имя блока вморожено в снапшот вопросов каждой формы:
// раунды, созданные до переименования, приходят с прежним «Общая оценка».
const OVERALL_BLOCKS = ['оценка за период', 'общая оценка', 'overall', 'general'];

export const isOverallBlock = (block?: string): boolean =>
  OVERALL_BLOCKS.includes((block || '').trim().toLowerCase());

export const overallFirst = <T extends { block?: string }>(rows: T[]): T[] => [
  ...rows.filter((r) => isOverallBlock(r.block)),
  ...rows.filter((r) => !isOverallBlock(r.block)),
];
